import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import {
  getPerformanceChartChrome,
  getPerformanceTooltipStyle,
} from '../styles/performanceMsiStyles';
import { usesAppStyleGrammar } from '../styles/msiStyleTokens';

const SOURCE_LIVE = 'live_realized';
const SOURCE_HOLDOUT = 'holdout_test';

function formatDateLabel(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d);
}

function CustomTooltip({ active, payload, label, theme }) {
  if (!active || !payload?.length) return null;
  const sourceLabel = (source) =>
    source === SOURCE_HOLDOUT ? 'Held-out validation' : 'Realized live';
  const tooltipStyle = usesAppStyleGrammar(theme)
    ? getPerformanceTooltipStyle(theme)
    : {
        background: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        boxShadow: theme.shadows[4],
      };
  return (
    <Box
      sx={{
        ...tooltipStyle,
        borderRadius: 1.5,
        px: 1.2,
        py: 0.8,
        fontSize: '0.72rem',
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, mb: 0.3 }}>
        {formatDateLabel(label)}
      </Typography>
      {payload.map((entry) => (
        <Box key={entry.dataKey} sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            {entry.name}: {Number.isFinite(entry.value) ? entry.value.toFixed(3) : '—'}
          </Typography>
        </Box>
      ))}
      {payload[0]?.payload?._source && (
        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.3 }}>
          {sourceLabel(payload[0].payload._source)}
        </Typography>
      )}
    </Box>
  );
}

export default function RecentPerformanceChart({
  liveRealizedRows = [],
  holdoutTestRows = [],
}) {
  const theme = useTheme();

  const { chartData, hasLive, hasHoldout, hasBaseline } = useMemo(() => {
    const byDate = {};

    for (const row of holdoutTestRows) {
      const d = row.prediction_as_of_date;
      if (!d) continue;
      byDate[d] = {
        date: d,
        holdout_model_rmse: row.model_rmse,
        baseline_rmse: row.baseline_rmse,
        _source: SOURCE_HOLDOUT,
      };
    }

    for (const row of liveRealizedRows) {
      const d = row.prediction_as_of_date;
      if (!d) continue;
      const existing = byDate[d] || { date: d };
      byDate[d] = {
        ...existing,
        live_model_rmse: row.model_rmse,
        baseline_rmse: row.baseline_rmse ?? existing.baseline_rmse,
        _source: existing.holdout_model_rmse != null ? 'both' : SOURCE_LIVE,
      };
    }

    const sorted = Object.values(byDate).sort((a, b) =>
      (a.date || '').localeCompare(b.date || ''),
    );
    return {
      chartData: sorted,
      hasLive: liveRealizedRows.length > 0,
      hasHoldout: holdoutTestRows.length > 0,
      hasBaseline: sorted.some((row) => row.baseline_rmse != null),
    };
  }, [liveRealizedRows, holdoutTestRows]);

  if (chartData.length === 0) return null;

  const modelLineColor = theme.palette.primary.main;
  const baselineLineColor = theme.palette.grey[500];
  const chartChrome = usesAppStyleGrammar(theme)
    ? getPerformanceChartChrome(theme)
    : {
        grid: alpha(theme.palette.divider, 0.4),
        mutedText: theme.palette.text.secondary,
        axis: theme.palette.divider,
      };

  return (
    <Box sx={{ width: '100%', mb: 1.5 }}>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartChrome.grid} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateLabel}
            tick={{ fontSize: 10, fill: chartChrome.mutedText }}
            axisLine={{ stroke: chartChrome.axis }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: chartChrome.mutedText }}
            axisLine={{ stroke: chartChrome.axis }}
            tickFormatter={(v) => v.toFixed(2)}
          />
          <RechartsTooltip content={<CustomTooltip theme={theme} />} />
          <Legend
            wrapperStyle={{ fontSize: '0.68rem', paddingTop: 4 }}
          />

          {hasLive && (
            <Line
              type="monotone"
              dataKey="live_model_rmse"
              name="Model RMSE (live)"
              stroke={modelLineColor}
              strokeWidth={2}
              dot={{ r: 2.5 }}
              connectNulls
            />
          )}
          {hasBaseline && (
            <Line
              type="monotone"
              dataKey="baseline_rmse"
              name="Baseline RMSE"
              stroke={baselineLineColor}
              strokeWidth={1.5}
              dot={{ r: 2 }}
              connectNulls
            />
          )}
          {hasHoldout && (
            <Line
              type="monotone"
              dataKey="holdout_model_rmse"
              name="Model RMSE (held-out)"
              stroke={modelLineColor}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ r: 2.5 }}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
